import React from 'react';
const { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } = React;

export interface ContextMenuItem {
    id: string;
    label: string;
    iconClass?: string;
    iconStyle?: React.CSSProperties;
    isDisabled?: boolean;
    items?: ContextMenuItem[];
    hasSeparatorBefore?: boolean;
}

interface ContextMenuProps {
    left: number;
    top: number;
    items: ContextMenuItem[];
    onItemClick: (itemId: string) => void;
    containerId?: string;
    onRequestClose?: () => void;
    margin?: number;
    zIndex?: number;
}

const ContextMenu = React.forwardRef<HTMLDivElement, ContextMenuProps>((props, forwardedRef) => {
    const { left, top, items, onItemClick, containerId, onRequestClose, margin = 10, zIndex = 6001 } = props;
    const [position, setPosition] = useState({ left, top });
    const [openSubmenuID, setOpenSubmenuID] = useState<string | null>(null);
    const [openSubmenuToLeft, setOpenSubmenuToLeft] = useState(false);
    const internalRef = useRef<HTMLDivElement | null>(null);

    const setRefs = useCallback((node: HTMLDivElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === 'function') {
            forwardedRef(node);
        } else if (forwardedRef) {
            (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
    }, [forwardedRef]);

    useLayoutEffect(() => {
        const el = internalRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        let clampedLeft = left;
        let clampedTop = top;
        const maxLeft = Math.max(window.innerWidth - rect.width - margin, margin);
        const maxTop = Math.max(window.innerHeight - rect.height - margin, margin);
        if (clampedLeft < margin) clampedLeft = margin;
        if (clampedTop < margin) clampedTop = margin;
        if (clampedLeft > maxLeft) clampedLeft = maxLeft;
        if (clampedTop > maxTop) clampedTop = maxTop;
        if (clampedLeft !== position.left || clampedTop !== position.top) {
            setPosition({ left: clampedLeft, top: clampedTop });
        }
    }, [left, top, margin]);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent | MouseEvent) => {
            if (!internalRef.current) return;
            if (!internalRef.current.contains(event.target as Node)) {
                onRequestClose?.();
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onRequestClose?.();
        };
        window.addEventListener('pointerdown', handlePointerDown);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onRequestClose]);

    const style = useMemo(() => ({
        transform: `translate3d(${position.left}px, ${position.top}px, 0)`,
        zIndex,
    }), [position.left, position.top, zIndex]);

    const openSubmenu = (itemID: string, element: HTMLLIElement) => {
        const estimatedSubmenuWidth = 210;
        setOpenSubmenuToLeft(
            element.getBoundingClientRect().right + estimatedSubmenuWidth + margin > window.innerWidth
        );
        setOpenSubmenuID(itemID);
    };

    const renderItem = (item: ContextMenuItem, isSubmenuItem = false) => {
        const hasSubmenu = !!item.items?.length;
        const isOpen = hasSubmenu && openSubmenuID === item.id;
        return (
            <li
                className={[
                    item.isDisabled ? 'disabled' : '',
                    hasSubmenu ? 'has-context-submenu' : '',
                    item.hasSeparatorBefore ? 'has-separator-before' : '',
                    isOpen ? 'submenu-open' : '',
                ].filter(Boolean).join(' ')}
                key={item.id}
                role="menuitem"
                tabIndex={item.isDisabled ? -1 : 0}
                aria-disabled={item.isDisabled || undefined}
                aria-haspopup={hasSubmenu || undefined}
                aria-expanded={hasSubmenu ? isOpen : undefined}
                onMouseEnter={event => {
                    if (hasSubmenu && !item.isDisabled) {
                        openSubmenu(item.id, event.currentTarget);
                    } else if (!isSubmenuItem) {
                        setOpenSubmenuID(null);
                    }
                }}
                onClick={event => {
                    event.stopPropagation();
                    if (item.isDisabled || hasSubmenu) return;
                    onItemClick(item.id);
                    onRequestClose?.();
                }}
                onKeyDown={event => {
                    if (item.isDisabled) return;
                    if (hasSubmenu && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowRight')) {
                        event.preventDefault();
                        openSubmenu(item.id, event.currentTarget);
                    } else if (!hasSubmenu && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onItemClick(item.id);
                        onRequestClose?.();
                    } else if (event.key === 'ArrowLeft' && isSubmenuItem) {
                        setOpenSubmenuID(null);
                    }
                }}
            >
                {item.iconClass && <i className={`icon ${item.iconClass}`} style={item.iconStyle} />}
                <span className="context-menu-label">{item.label}</span>
                {hasSubmenu && <i className="fa fa-angle-right context-submenu-arrow" aria-hidden="true" />}
                {isOpen && (
                    <ul
                        className={`context-menu context-submenu${openSubmenuToLeft ? ' opens-left' : ''}`}
                        role="menu"
                        onMouseLeave={() => setOpenSubmenuID(null)}
                    >
                        {item.items!.map(child => renderItem(child, true))}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <div
            className={`context-menu-container${items?.some(item => item.items?.length) ? ' has-context-submenus' : ''}`}
            ref={setRefs}
            style={style}
            onContextMenu={(evt) => evt.preventDefault()}
        >
            <div className="view-context-menu" id={containerId}>
                <ul className="context-menu" role="menu">
                    {items?.map(item => renderItem(item))}
                </ul>
            </div>
        </div>
    );
});

export default ContextMenu;
